import { action, computed, flow, observable, makeObservable, runInAction } from "mobx"
import { rootStore } from "store/RootStore"
import { languageName, countryName} from "utils/IntlManager"
import { adjustUtc, moment } from "store/util/moment"
import { TIME_FORMAT_BACKEND } from "store/model/Time"
import { appFeatures } from "AppFeatures"
import { channelId, eventId, startTs, endTs } from "utils/EventHelpers"
import { EventDetailsMode } from "@quickline/coco"

import { getCoCoModule, wasmToPlainObject } from "store/page/repo/CoCoModule"
const CoCo = await getCoCoModule()

export class MsEpg {
	programs = []
	epgDayCache = new Map()
	epgDay = null
	channelDayPrograms = []
	channelDayCache = new Map()
	epgChannelDay = null
	progsNow = []

	constructor() {
		makeObservable(this, {
			channels: computed,
			programs: observable.shallow,
			// MTVW-536: need to be observable
			epgDay: observable,
			epgDayCache: observable.shallow,
			isReady: computed,
			getProgramsForDayAsync: action,
			// MTVW-340
			channelDayPrograms: observable.shallow,
			channelDayCache: observable.shallow,
			isChannelDayReady: computed,
			getProgramsForChannelAsyncAllDays: action,
			getProgramsForChannelAsync: action,
			getEventByChannelTime: action,
		})
	}

	get channels() {
		return rootStore.channelsService.channels
	}

	get isReady() {
		const result = appFeatures.traxisAvailable ? (this.channels.length > 0 && this.epgDayCache.get(this.epgDay) !== undefined) : (this.channels.length > 0 && this.epgDayCache.get(this.epgDay) !== undefined)
		return result
	}

	// MTVW-340
	get isChannelDayReady() {
		const result = appFeatures.traxisAvailable ? (this.channels.length > 0 && this.channelDayCache.get(this.epgChannelDay) !== undefined) : (this.channels.length > 0 && this.channelDayCache.get(this.epgChannelDay) !== undefined)
		//console.debug("isChannelDayReady isReady %s", result)
		return result
	}

	getProgramsForDayAsync = flow(function* (day) {
		this.epgDay = day
		try {
			const cacheItem = this.epgDayCache.get(day)
			if (cacheItem && Array.isArray(cacheItem.progs)) {
				this.programs = cacheItem.progs
			}
			else {
				// set to null, otherwise overlapping day events would be displayed
				//this.programs = null
				// set fake data instead
				//const start = performance.now()
				this.programs = rootStore.epgCoco.fakeProgramsDay(day, this.channels)
				//console.debug("FAKE GEN %s ms", performance.now() - start)
				this.epgDayCache.set(day, { progs: this.programs, etag: null })
				//console.debug("FAKE %o", rootStore.epgCoco.fakeProgramsDay(day, this.channels))
			}
			const result =  yield rootStore.epgCoco.programsForDayAsync(day, cacheItem ? cacheItem.etag : null)
			console.debug("getProgramsForDayAsync", result )
			// Apply workaround for QLMS-850
			rootStore.epgCoco.qlms_850(result.progs, day)
			// in case the etag did not change progs is null and we keep the cache item
			if (result.progs) this.epgDayCache.set(day, result)
			// enable for testing fake data
			//this.epgDayCache.set(day, { progs: rootStore.epgCoco.fakeProgramsDay(day, this.channels), etag: null })

			// Now store the programs in the object. Avoid visual artifact, set only if no cache item was exsisting
			if (!cacheItem) {
				//this.programs = this.epgDayCache.get(day).progs
				// return the last requested day (click on not loaded day and click immediately on JETZT)
				this.programs = this.epgDayCache.get(this.epgDay).progs
			}
			return result.progs
		}
		catch (e) {
			console.error("getProgramsForDayAsync", e)
			return null
		}
	})

	// MTVW-340 / unused
	getProgramsForChannelDayAsync = flow(function* (channel, day) {
		const progs = []
		try {
			const dayProgs = yield rootStore.epgCoco.programsForChannelAsync(channel, day)
			dayProgs?.forEach(j => {
				if (j.AvailabilityStartDateFormat("YYYYMMDD") === day) {
					progs.push(j)
				}
			})
		}
		catch (e) {
			console.error("getProgramsForChannelDayAsync", e)
		}
		//console.debug("getProgramsForChannelDayAsync %o", progs)
		return progs
	})

	// MTVW-1157: performance improvement: use getProgramsForChannelAsyncAllDays
	getProgramsForChannelAsyncAllDays = flow(function* (channel) {
		const progs = yield rootStore.epgCoco.programsForChannelAsyncAllDays(channel)
		this.epgChannelDay = channel
		this.channelDayPrograms = progs.events
		return progs
	})

	// MTVW-340
	getProgramsForChannelAsync = flow(function* (channel, daysBefore, daysAfter) {
		let progs = []
		try {
			const cacheItem = this.channelDayCache.get(channel)
			if (cacheItem && Array.isArray(cacheItem)) {
				this.channelDayPrograms = cacheItem
				//console.debug("CACHE ITEM")
			}
			else {
				// invalidate data
				this.channelDayPrograms = null
				//console.debug("NO CACHE ITEM")
			}
			for (let i = -daysBefore; i <= daysAfter; i++) {
				// MTVW-414 
				//const day = moment().utc().add(i, "days").format("YYYYMMDD")
				const day = adjustUtc().add(i, "days").format("YYYYMMDD")
				const dayProgs = yield rootStore.epgCoco.programsForChannelAsync(channel, day)
				// WORKAROUND FOR: Error: C:\git\quickline\tmp4\tv-web\src\store\page\repo\MsEpg.js:
				// Internal Babel error: path is not in loop.Please report this as a bug.
				/*
				dayProgs?.forEach(j => {
					if (j.AvailabilityStartDateFormat("YYYYMMDD") === day) {
						progs.push(j)
					}
				})
				*/
				for (let j = 0; j < dayProgs.length; j++) {
					if (dayProgs[j].AvailabilityStartDateFormat("YYYYMMDD") === day) progs.push(dayProgs[j])
				}
			}
			this.channelDayCache.set(channel, progs)
			if (!cacheItem) {
				//console.debug("SET CACHE ITEM")
				this.channelDayPrograms = this.channelDayCache.get(channel)
			}
		}
		catch (e) {
			console.error("getProgramsForChannelAsync", e)
		}
		console.debug("getProgramsForChannelAsync %o", progs)
		return progs
	})

	// MTVW-536: flow function
	preloadDay = flow(function* (day) {
		const res = yield rootStore.epgCoco.programsForDayAsync(day, null)
		this.epgDayCache.set(day, res)
	})

	// MTVW-487: replace traxis rootStore.api.GetEventByChannelTime with epgMS implementation
	getEventByChannelTime = flow(function* (channelId, eventTime) {
		console.debug("chnlTime entry", channelId, eventTime, moment(eventTime).format(TIME_FORMAT_BACKEND))
		const day = moment(eventTime).utc().format("YYYYMMDD")
		const cacheItem = this.epgDayCache.get(day)
		//console.debug("day %o, time %o, cache %o", day, eventTime, cacheItem)
		let progs = null
		if (cacheItem && Array.isArray(cacheItem.progs)) {
			progs = cacheItem.progs
		}
		else {
			yield this.getProgramsForDayAsync(day)
			progs = this.programs
		}
		progs = progs?.filter(i => i.channelId === channelId)[0]
		//console.debug("PROGS %o", progs)
		if (progs) {
			// MTVW-506: <= i.event.AvailabilityEndTs changed to < i.event.AvailabilityEndTs
			const events = progs.Events.filter(i => eventTime >= startTs(i.event) && eventTime < endTs(i.event))
			progs.Events.forEach(i => {
				console.debug("chnlTime", i?.Title?.Name, i?.EpisodeName ? "• " + i?.EpisodeName : "", i?.AvailabilityStartDate.format(TIME_FORMAT_BACKEND), i?.AvailabilityEndDate.format(TIME_FORMAT_BACKEND))
			})
			const event = events?.[0]
			if (events.length > 1) console.warn("chnlTime MORE THAN 1 EVENT", events)
			console.debug("chnlTime selected", events?.length, event?.Title?.Name, event?.EpisodeName ? "• " + event?.EpisodeName : "", event?.AvailabilityStartDate.format(TIME_FORMAT_BACKEND), event?.AvailabilityEndDate.format(TIME_FORMAT_BACKEND))			//console.debug("EVENT= %o", event)
			// MTVW-1096: replay stream start performance improvement
			//return yield this.getEventDetailsAsync(eventId(event?.event), EventDetailsMode.Full)
			const liteEvent = yield this.getEventDetailsAsync(eventId(event?.event), EventDetailsMode.Lite)
			rootStore.page.Player.source.GetEvent = liteEvent
			this.getEventDetailsAsync(eventId(event?.event), EventDetailsMode.Full).then(fullEvent => {
				runInAction(() => {
					rootStore.page.Player.source.GetEvent = fullEvent
				})
			})
			return liteEvent
		}
		return null
	})

	//MTVW-272
	/*
	_getTraxisEventDetails = flow(function* (eventId) {
		let traxisEvent = yield rootStore._root.api
			.GetEventById({ _idCache: "OverlayEventDetails", EventId: eventId })
			.abortReq()
			.fetchDataAsync()
		//console.debug("traxisEvent %o", traxisEvent)
		return traxisEvent
	})
	*/

	/*
	_getTraxisCredits(traxisCredits, type) {
		const result = []
		if (traxisCredits && traxisCredits?.length > 0) {
			traxisCredits.forEach(i => {
				if (i.type === type) {
					result.push(i.givenName + " " + i.familyName)
				}
			})
		}
		return result
	}

	_getTraxisGenres(traxisGenres) {
		const result = []
		if (traxisGenres && traxisGenres?.length > 0) {
			traxisGenres.forEach(i => {
				result.push(i.Value)
			})
		}
		return result
	}
	*/

	// MTVW-1075
	getEventDetailsCocoAsync = flow(function* (eventId, eventDetailsMode = EventDetailsMode.Lite) {
		let result = null
		let msError = null
		try {
			result = yield rootStore.epgCoco.eventDetailsAsync(eventId, eventDetailsMode)
		}
		catch (err) {
			// don't display error message (for recordings)
			runInAction(() => {
				rootStore.page.Player.error = null
			})
			msError = err
			console.error("getEventDetailsCocoAsync error", err)
		}
		if (msError) {
			result = { error: msError }
		}
		return result
	})

	getEventDetailsAsync = flow(function* (eventId, eventDetailsMode = EventDetailsMode.Lite) {
		const result = yield this.getEventDetailsCocoAsync(eventId, eventDetailsMode)
		return result
	})

	// MTVW-246
	getProgramsForNow = flow(function* () {
		let progs = []
		try {
			progs = yield rootStore.epgCoco.programsForNowAsync()
			//console.debug("programsForNow", progs)
			this.progsNow = progs
			return progs
		}
		catch (e) {
			console.error("getProgramsForNow", e)
			return this.progsNow
		}
	})


	// GT12: MTVW-460, MTVW-502: Channels MS flags
	/*
	hasReplay:
	----------
		True if the channel has replay. Set to false if no replay content is avaible

		if isSubscribed is false:
			set to false
		else if user cannot fast forward or rewind:
			set to false
		else if maxReplayDuration <= PT2H:
			set to false
		else if recordingAndReplayAllowedSince == 0:
			set to false
		else:
			set to true

	maxReplayDuration:
	------------------
	The maximal allowed replay duration in ISO 8601 format. Usually set to P7D, P1DT6H, PT2H. If set to PT0S, no replay is allowed at all. This field never contains an empty string. The actual replay duration depends also on the recording opt-in date of the user.

		if isSubscribed is false:
			replay is not allowed.
		else if maxReplayDuration is PT0S:
			replay is not allowed.
		else if recordingAndReplayAllowedTill <= now
			replay is not allowed.
		else if recordingAndReplayAllowedSince == 0:
			replay is allowed since now-PT2H.
		else if now-maxReplayDuration < recordingAndReplayAllowedSince:
			replay is allowed since recordingAndReplayAllowedSince.
		else:
			replay is allwed since now-maxReplayDuration.
	*/

	// MTVW-631: add parameter to override maxReplayDuration
	_allowedSince(channelId, eventStart, eventEnd, isRecording = false, maxReplayDuration = null) {
		const channel = rootStore.channelsService.getChannel(channelId)
		const now = moment().utc().valueOf()
		const enablerFlag = isRecording ? channel?.hasNPVR : channel?.hasReplay
		const _maxReplayDuration = maxReplayDuration ? maxReplayDuration : channel?.maxReplayDuration
		//console.debug("maxReplayDuration", _maxReplayDuration, maxReplayDuration)

		// MTVW-1043: Eliminate rollingBufferId
		// if (enablerFlag && channel?.rollingBufferId?.length > 0) {
		if (enablerFlag) {
			let allowedSince = now - moment.duration(_maxReplayDuration).valueOf()
			if (!maxReplayDuration && channel?.recordingAndReplayAllowedSince === 0) {
				allowedSince = now - moment.duration("PT2H").valueOf()
			}
			else if (now - moment.duration(_maxReplayDuration).valueOf() < channel?.recordingAndReplayAllowedSince) {
				allowedSince = channel?.recordingAndReplayAllowedSince
			}
			// else allowedSince = now - _maxReplayDuration
			return allowedSince
		}
		else return now
	}

	replayOrRecordingAllowedSince(channelId, eventStart, eventEnd, isRecording = false) {
		return this._allowedSince(channelId, eventStart, eventEnd, isRecording)
	}

	// MTVW-631: add parameter to override maxReplayDuration
	isReplayAllowed(channelId, eventStart, eventEnd, isRecording = false, maxReplayDuration = null) {
		//console.debug("isReplayAllowed", channelId, eventStart, eventEnd, channelId, isRecording, rootStore.channelsService.getChannel(channelId))
		return eventStart >= this._allowedSince(channelId, eventStart, eventEnd, isRecording, maxReplayDuration)
		/*
		return (isRecording ? true : eventStart >= moment().utc().valueOf() - moment.duration(channel?.maxReplayDuration).valueOf())
			&& (eventStart >= channel?.recordingAndReplayAllowedSince)
			&& (eventEnd <= channel?.recordingAndReplayAllowedTill)
		//&& (channel?.hasReplay || isRecording)
		*/
	}

	// GT12: MTVW-460, MTVW-502: Channels MS flags
	/*
	hasNPVR:
	--------
	If set to true, it is allowed to book recordings on this channel. Set to false if no recordings may be booked.
	
	if isSubscribed is false:
		booking of recordings is not allowed.
	else if maxReplayDuration is PT0S or isRecordingAllowed is false:
		booking of recordings is not allowed.
	else if recordingAndReplayAllowedTill <= now
		booking of recordings is not allowed.
	else if recordingAndReplayAllowedSince == 0:
		booking of recordings is allowed since now-PT2H.
	else if now-maxReplayDuration < recordingAndReplayAllowedSince:
		booking of recordings is allowed since recordingAndReplayAllowedSince.
	else:
		booking of recordings is allwed since now-maxReplayDuration.
	*/
	isRecordingAllowed(channelId, eventStart, eventEnd) {
		//console.debug("isSTBOnly", rootStore.channelsService.getChannel(channelId)?.isSTBOnly, rootStore.channelsService.getChannel(channelId))
		return eventStart >= this._allowedSince(channelId, eventStart, eventEnd, true) &&
			!rootStore.channelsService.getChannel(channelId)?.isSTBOnly
	}

	events(channelId) {
		if (!this.isReady || typeof this.programs?.find !== "function") return []
		const result = this.programs?.find(i => i.channelId === channelId)
		//console.debug("RESULT for channel %s, %o", channelId, result)
		return result ? result.Events : []
	}

	// MTVW-1020: dayEvents to prevent disappearing TvGuide if an older event from a different day is watched from the details page
	dayEvents(channelId, day) {
		// we should have a cachedItem for the given day, otherwise something is wrong
		const cacheItem = this.epgDayCache.get(day)
		if (cacheItem && Array.isArray(cacheItem.progs)) {
			const result = cacheItem.progs.find(i => i.channelId === channelId)
			return result ? result.Events : []
		}
		else return []
	}

	panelInfo(event) {
		if (!this.isReady || typeof this.channels?.find !== "function") return {}
		//console.debug("isOptedIn", this.channels)
		const result = this.channels?.find(i => i.id === channelId(event))
		/*
		console.debug("channel %s, opt-in date %s, hasReplay %s, maxReplay %s, replayAllowed %s", channelId(event), result?.recordingAndReplayAllowedSince, result?.hasReplay,
			result?.maxReplayDuration, rootStore.page.OverlayEventDetails.isPlayButtonEnabled)//this.isReplayAllowed(channelId(event), event?.AvailabilityStartDateTs, event?.AvailabilityEndDateTs)
		*/
		const marker = rootStore.page.Recordings.Manager.getMarker(eventId(event))
		const isReplayAllowed = this.isReplayAllowed(channelId(event), startTs(event), endTs(event), false) || marker?.State === "Complete"
		return { isSubscribed: result?.isSubscribed,
					optinDate: result ? result.recordingAndReplayAllowedSince : 0,
				   hasReplay: result?.hasReplay,
					replayAllowed: isReplayAllowed/*rootStore.page.OverlayEventDetails.isPlayButtonEnabled*/,
					maxReplayDuration: result?.maxReplayDuration }
	}

	_init() {
		//console.debug("in _init MsEpg %o", new Error().stack)
		// done in TV Guide
		//rootStore.channelsService.getChannelsAsync()
		//this.getProgramsForDayAsync(moment().utc().format("YYYYMMDD"))
		//this.getProgramsForNow()
	}
}
